import { useState, useMemo, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Globe, Mail, Shield, Lock, Eye, EyeOff, Loader2, Check, UserCircle } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { api, ApiError } from '../lib/api';
import { getTimezones, timezoneLabel, detectTimezone } from '../lib/timezones';

type Connection = { provider: string; email: string | null; createdAt: string };

function GoogleIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908C16.658 14.072 17.64 11.837 17.64 9.2z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
      <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  );
}

export function EditProfile() {
  const { user, refresh } = useAuth();
  const hasPassword = user?.hasPassword ?? true;

  /* ── linked accounts ── */
  const [searchParams] = useSearchParams();
  const linkedParam = searchParams.get('linked');
  const { data: connData } = useQuery({
    queryKey: ['auth', 'connections'],
    queryFn: () => api<{ connections: Connection[] }>('/api/auth/connections'),
  });
  const googleConn = connData?.connections.find((c) => c.provider === 'google') ?? null;
  const linkedMessage = (() => {
    switch (linkedParam) {
      case 'ok':      return { ok: true,  text: 'Cuenta de Google vinculada correctamente.' };
      case 'already': return { ok: true,  text: 'Ya tenías una cuenta de Google vinculada.' };
      case 'taken':   return { ok: false, text: 'Esa cuenta de Google ya está vinculada a otra cuenta de Zettapost.' };
      case null:      return null;
      default:        return { ok: false, text: 'No se pudo vincular la cuenta de Google. Inténtalo de nuevo.' };
    }
  })();

  /* ── account info ── */
  const [timezone, setTimezone] = useState<string>(user?.timezone ?? detectTimezone());
  const [saving, setSaving]     = useState(false);
  const [okMsg, setOkMsg]       = useState<string | null>(null);
  const [error, setError]       = useState<string | null>(null);

  const timezoneOptions = useMemo(
    () => getTimezones().map((tz) => ({ value: tz, label: timezoneLabel(tz) })),
    [],
  );

  const dirty = user ? timezone !== user.timezone : false;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setOkMsg(null);
    setSaving(true);
    try {
      await api('/api/auth/me', { method: 'PATCH', body: JSON.stringify({ timezone }) });
      await refresh();
      setOkMsg('Cambios guardados correctamente.');
    } catch (err) {
      setError(
        err instanceof ApiError
          ? 'No se pudieron guardar los cambios. Revisa los datos.'
          : 'Algo ha fallado. Inténtalo de nuevo.',
      );
    } finally {
      setSaving(false);
    }
  }

  /* ── password ── */
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword]         = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw]                   = useState(false);
  const [pwSaving, setPwSaving]               = useState(false);
  const [pwOk, setPwOk]                       = useState<string | null>(null);
  const [pwError, setPwError]                 = useState<string | null>(null);

  const newValid = newPassword.length >= 8;
  const matches  = newPassword.length > 0 && newPassword === confirmPassword;
  const canSubmitPw =
    newValid && matches && (!hasPassword || currentPassword.length > 0);

  async function onPasswordSubmit(e: FormEvent) {
    e.preventDefault();
    setPwError(null);
    setPwOk(null);
    if (!newValid) { setPwError('La nueva contraseña debe tener al menos 8 caracteres.'); return; }
    if (!matches)  { setPwError('Las contraseñas no coinciden.'); return; }
    setPwSaving(true);
    try {
      await api('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({
          currentPassword: hasPassword ? currentPassword : undefined,
          newPassword,
        }),
      });
      await refresh();
      setPwOk(hasPassword ? 'Contraseña actualizada correctamente.' : 'Contraseña establecida correctamente.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'WRONG_PASSWORD') {
        setPwError('La contraseña actual es incorrecta.');
      } else if (err instanceof ApiError && err.code === 'CURRENT_PASSWORD_REQUIRED') {
        setPwError('Introduce tu contraseña actual.');
      } else if (err instanceof ApiError && err.code === 'RATE_LIMITED') {
        setPwError('Demasiados intentos. Espera unos minutos.');
      } else {
        setPwError('No se pudo cambiar la contraseña. Inténtalo de nuevo.');
      }
    } finally {
      setPwSaving(false);
    }
  }

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><UserCircle size={22} className="text-brand-600" /> Editar perfil</h1>
        <p className="mt-1 text-sm text-slate-500">Gestiona la información de tu cuenta.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 items-start">
      {/* ── account info ── */}
      <form onSubmit={onSubmit} className="card space-y-5">
        <h2 className="font-semibold text-slate-800">Información de cuenta</h2>

        {/* email — read-only */}
        <div>
          <label className="label">Email</label>
          <div className="relative">
            <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={user?.email ?? ''}
              disabled
              className="input pl-10 bg-slate-50 text-slate-500 cursor-not-allowed"
            />
          </div>
        </div>

        {/* role — read-only */}
        <div>
          <label className="label">Rol</label>
          <div className="relative">
            <Shield size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={user?.role === 'admin' ? 'Administrador' : 'Usuario'}
              disabled
              className="input pl-10 bg-slate-50 text-slate-500 cursor-not-allowed"
            />
          </div>
        </div>

        {/* timezone */}
        <div>
          <label className="label" htmlFor="timezone">Zona horaria</label>
          <div className="relative">
            <Globe size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <select
              id="timezone"
              value={timezone}
              onChange={(e) => { setTimezone(e.target.value); setOkMsg(null); }}
              className="input pl-10"
            >
              {timezoneOptions.map((tz) => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
          </div>
          <p className="mt-1 text-xs text-slate-400">
            Se usa para programar y mostrar tus publicaciones en tu hora local.
          </p>
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
            {error}
          </p>
        )}
        {okMsg && (
          <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2 flex items-center gap-1.5">
            <Check size={14} /> {okMsg}
          </p>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving || !dirty}
            className="btn-primary gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </form>

      <div className="space-y-6">
      {/* ── password ── */}
      <form onSubmit={onPasswordSubmit} className="card space-y-5">
        {/* Lets the password manager know which account the new password belongs to. */}
        <input type="email" name="username" autoComplete="username" value={user?.email ?? ''} readOnly hidden />
        <div>
          <h2 className="font-semibold text-slate-800">
            {hasPassword ? 'Cambiar contraseña' : 'Establecer contraseña'}
          </h2>
          {!hasPassword && (
            <p className="mt-1 text-xs text-slate-400">
              Tu cuenta usa Google para acceder. Añade una contraseña para poder
              entrar también con tu email.
            </p>
          )}
        </div>

        {/* current password — only if the account already has one */}
        {hasPassword && (
          <div>
            <label className="label" htmlFor="currentPassword">Contraseña actual</label>
            <div className="relative">
              <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                id="currentPassword"
                name="currentPassword"
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => { setCurrentPassword(e.target.value); setPwOk(null); }}
                className="input pl-10"
              />
            </div>
          </div>
        )}

        {/* new password */}
        <div>
          <label className="label" htmlFor="newPassword">Nueva contraseña</label>
          <div className="relative">
            <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              id="newPassword"
              name="newPassword"
              type={showPw ? 'text' : 'password'}
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => { setNewPassword(e.target.value); setPwOk(null); }}
              className="input pl-10 pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition"
              tabIndex={-1}
            >
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {newPassword.length > 0 && !newValid && (
            <p className="mt-1 text-xs text-amber-600">Mínimo 8 caracteres.</p>
          )}
        </div>

        {/* confirm */}
        <div>
          <label className="label" htmlFor="confirmPassword">Repite la nueva contraseña</label>
          <div className="relative">
            <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              id="confirmPassword"
              name="confirmPassword"
              type={showPw ? 'text' : 'password'}
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setPwOk(null); }}
              className="input pl-10"
            />
          </div>
          {confirmPassword.length > 0 && !matches && (
            <p className="mt-1 text-xs text-amber-600">Las contraseñas no coinciden.</p>
          )}
        </div>

        {pwError && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
            {pwError}
          </p>
        )}
        {pwOk && (
          <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2 flex items-center gap-1.5">
            <Check size={14} /> {pwOk}
          </p>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={pwSaving || !canSubmitPw}
            className="btn-primary gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {pwSaving && <Loader2 size={14} className="animate-spin" />}
            {pwSaving
              ? 'Guardando…'
              : hasPassword ? 'Cambiar contraseña' : 'Establecer contraseña'}
          </button>
        </div>
      </form>

      {/* ── linked accounts ── */}
      <div className="card space-y-4">
        <div>
          <h2 className="font-semibold text-slate-800">Cuentas vinculadas</h2>
          <p className="mt-1 text-xs text-slate-400">
            Vincula tu cuenta de Google para poder acceder también con un clic.
          </p>
        </div>

        {linkedMessage && (
          <p className={
            linkedMessage.ok
              ? 'text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2 flex items-center gap-1.5'
              : 'text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2'
          }>
            {linkedMessage.ok && <Check size={14} />}
            {linkedMessage.text}
          </p>
        )}

        <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3">
          <div className="flex items-center gap-3">
            <GoogleIcon />
            <div>
              <p className="text-sm font-medium text-slate-700">Google</p>
              <p className="text-xs text-slate-400">
                {googleConn ? (googleConn.email ?? 'Cuenta vinculada') : 'No vinculada'}
              </p>
            </div>
          </div>
          {googleConn ? (
            <span
              className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1"
              title="Por seguridad, las cuentas vinculadas no se pueden desvincular"
            >
              <Lock size={11} /> Vinculada
            </span>
          ) : (
            <button
              type="button"
              onClick={() => { window.location.href = '/api/auth/google/link'; }}
              className="inline-flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition shrink-0"
            >
              <GoogleIcon size={15} /> Vincular
            </button>
          )}
        </div>

        {googleConn && (
          <p className="text-xs text-slate-400 flex items-center gap-1.5">
            <Lock size={11} /> Por seguridad, una cuenta vinculada no se puede desvincular.
          </p>
        )}
      </div>
      </div>
      </div>
    </div>
  );
}
